import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Box, Collapse, IconButton, Typography, List, ListItem } from '@mui/material';
import { Close, Warning } from '@mui/icons-material';
import { isLocalStorageAvailable, getStorageBlockedMessage } from '@/utils/storageCheck';

/**
 * Shows a warning banner when localStorage is blocked by browser privacy settings.
 * This helps users understand why authentication isn't working.
 */
export const StorageBlockedWarning = () => {
	const [isBlocked, setIsBlocked] = useState(false);
	const [dismissed, setDismissed] = useState(false);
	const [browserInfo, setBrowserInfo] = useState(null);

	useEffect(() => {
		const blocked = !isLocalStorageAvailable();
		setIsBlocked(blocked);
		if (blocked) {
			setBrowserInfo(getStorageBlockedMessage());
		}
	}, []);

	if (!isBlocked || dismissed) {
		return null;
	}

	return (
		<Collapse in={!dismissed}>
			<Alert
				severity="warning"
				icon={<Warning />}
				action={
					<IconButton
						aria-label="close"
						color="inherit"
						size="small"
						onClick={() => setDismissed(true)}
					>
						<Close fontSize="inherit" />
					</IconButton>
				}
				sx={{
					mb: 2,
					'& .MuiAlert-message': { width: '100%' }
				}}
			>
				<AlertTitle>Browser Storage Blocked</AlertTitle>
				<Typography variant="body2" sx={{ mb: 1 }}>
					Your browser is blocking storage access, which prevents login from working properly.
					This is usually caused by <strong>Tracking Prevention</strong> settings.
				</Typography>
				
				{browserInfo && (
					<Box sx={{ mt: 1 }}>
						<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
							To fix this in {browserInfo.browser}:
						</Typography>
						<List dense sx={{ pl: 2, '& .MuiListItem-root': { py: 0.25 } }}>
							{browserInfo.instructions.map((instruction, index) => (
								<ListItem key={index} sx={{ display: 'list-item', listStyleType: 'decimal' }}>
									<Typography variant="body2">{instruction}</Typography>
								</ListItem>
							))}
						</List>
					</Box>
				)}
			</Alert>
		</Collapse>
	);
};

export default StorageBlockedWarning;
